import { Controller } from "@hotwired/stimulus"

// F1 24/25 team ids are 0–9; F1 26 reissued the grid as 476–486 (per the
// official UDP spec appendix) so both ranges coexist here.
const F1_TEAM_COLORS = {
  // F1 24 / F1 25
  0: "#27F4D2",
  1: "#E8002D",
  2: "#3671C6",
  3: "#64C4FF",
  4: "#229971",
  5: "#FF87BC",
  6: "#6692FF",
  7: "#B6BABD",
  8: "#FF8000",
  9: "#52E252",
  // F1 26 (2026 Season Pack). 476–484 mirror the F1 25 livery colors for the
  // 9 returning teams; 485 (Audi) and 486 (Cadillac) use approximations of
  // their announced 2026 liveries.
  476: "#27F4D2", // Mercedes
  477: "#E8002D", // Ferrari
  478: "#3671C6", // Red Bull
  479: "#64C4FF", // Williams
  480: "#229971", // Aston Martin
  481: "#FF87BC", // Alpine
  482: "#6692FF", // RB
  483: "#B6BABD", // Haas
  484: "#FF8000", // McLaren
  485: "#00583E", // Audi (racing green over black)
  486: "#A8A8A8", // Cadillac (silver / black)
}

export default class extends Controller {
  static targets = ["table"]
  static values = { trackedCarIndex: { type: Number, default: 0 } }

  connect() {
    this.personalBestLaps = {}
    this.personalBestSectors = {}
    this.overallBestLap = Infinity
    this.overallBestSectors = { s1: Infinity, s2: Infinity, s3: Infinity }
    this.lastPitLap = {}
    this.sessionData = {}
  }

  update(event) {
    const data = event.detail
    if (!data.cars) return

    // Extract session-level fields
    this.sessionData = {
      session_time_left: data.session_time_left,
      session_duration: data.session_duration,
      total_laps: data.total_laps,
      safety_car_status: data.safety_car_status,
      weather: data.weather,
      air_temperature: data.air_temperature,
      track_temperature: data.track_temperature,
    }

    if (data.tracked_car_index !== undefined) {
      this.trackedCarIndexValue = data.tracked_car_index
    }

    const cars = [...data.cars].sort((a, b) => a.position - b.position)
    this.updateBests(cars)
    this.render(cars)
  }

  updateBests(cars) {
    for (const car of cars) {
      const idx = car.car_index

      // Best lap
      const lapMs =
        car.best_lap_time_ms ||
        (car.last_lap_time_ms > 0 ? car.last_lap_time_ms : 0)
      if (lapMs > 0) {
        if (!this.personalBestLaps[idx] || lapMs < this.personalBestLaps[idx]) {
          this.personalBestLaps[idx] = lapMs
        }
        if (this.personalBestLaps[idx] < this.overallBestLap) {
          this.overallBestLap = this.personalBestLaps[idx]
        }
      }

      // Best sectors
      if (!this.personalBestSectors[idx]) {
        this.personalBestSectors[idx] = {
          s1: Infinity,
          s2: Infinity,
          s3: Infinity,
        }
      }
      if (car.sector1_ms && car.sector1_ms > 0) {
        if (car.sector1_ms < this.personalBestSectors[idx].s1) {
          this.personalBestSectors[idx].s1 = car.sector1_ms
        }
        if (car.sector1_ms < this.overallBestSectors.s1) {
          this.overallBestSectors.s1 = car.sector1_ms
        }
      }
      if (car.sector2_ms && car.sector2_ms > 0) {
        if (car.sector2_ms < this.personalBestSectors[idx].s2) {
          this.personalBestSectors[idx].s2 = car.sector2_ms
        }
        if (car.sector2_ms < this.overallBestSectors.s2) {
          this.overallBestSectors.s2 = car.sector2_ms
        }
      }
      if (car.sector3_ms && car.sector3_ms > 0) {
        if (car.sector3_ms < this.personalBestSectors[idx].s3) {
          this.personalBestSectors[idx].s3 = car.sector3_ms
        }
        if (car.sector3_ms < this.overallBestSectors.s3) {
          this.overallBestSectors.s3 = car.sector3_ms
        }
      }

      // Pit tracking
      if (car.pit_status > 0) {
        this.lastPitLap[idx] = car.current_lap
      }
    }
  }

  sectorColor(value, carIndex, sectorKey) {
    if (!value || value <= 0) return "text-white/60"
    const pb = this.personalBestSectors[carIndex]
    if (value <= this.overallBestSectors[sectorKey])
      return "text-fuchsia-500 font-bold"
    if (pb && value <= pb[sectorKey]) return "text-green-400 font-bold"
    return "text-yellow-400"
  }

  lapColor(lapTimeMs, carIndex) {
    if (!lapTimeMs || lapTimeMs <= 0) return "text-white/60"
    if (lapTimeMs <= this.overallBestLap) return "text-fuchsia-500 font-bold"
    if (
      this.personalBestLaps[carIndex] &&
      lapTimeMs <= this.personalBestLaps[carIndex]
    )
      return "text-green-400 font-bold"
    return "text-yellow-400"
  }

  render(cars) {
    if (!this.hasTableTarget) return

    const topBar = this.renderTopBar(cars)
    const rows = cars.map((car) => this.renderRow(car)).join("")

    this.tableTarget.innerHTML = `
      ${topBar}
      <table class="w-full" style="table-layout:fixed">
        <colgroup>
          <col style="width:32px">
          <col>
          <col style="width:40px">
          <col style="width:44px">
          <col style="width:72px">
          <col style="width:72px">
          <col style="width:72px">
          <col style="width:72px">
          <col style="width:64px">
          <col style="width:64px">
          <col style="width:64px">
        </colgroup>
        <thead>
          <tr class="border-b border-white/10">
            <th class="py-2 px-2 text-center text-[10px] font-semibold text-white/40 uppercase">P</th>
            <th class="py-2 px-2 text-left text-[10px] font-semibold text-white/40 uppercase">Driver</th>
            <th class="py-2 px-2 text-center text-[10px] font-semibold text-white/40 uppercase">Pit</th>
            <th class="py-2 px-2 text-center text-[10px] font-semibold text-white/40 uppercase">Lap</th>
            <th class="py-2 px-2 text-right text-[10px] font-semibold text-white/40 uppercase">Gap</th>
            <th class="py-2 px-2 text-right text-[10px] font-semibold text-white/40 uppercase">Int</th>
            <th class="py-2 px-2 text-right text-[10px] font-semibold text-white/40 uppercase">Best</th>
            <th class="py-2 px-2 text-right text-[10px] font-semibold text-white/40 uppercase">Last</th>
            <th class="py-2 px-2 text-right text-[10px] font-semibold text-white/40 uppercase">S1</th>
            <th class="py-2 px-2 text-right text-[10px] font-semibold text-white/40 uppercase">S2</th>
            <th class="py-2 px-2 text-right text-[10px] font-semibold text-white/40 uppercase">S3</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`
  }

  renderTopBar(cars) {
    const sd = this.sessionData
    let leftInfo = ""

    if (sd.total_laps && sd.total_laps > 0) {
      const leaderLap = cars.length > 0 ? cars[0].current_lap : 0
      leftInfo = `<span class="text-xs font-mono text-white">LAP ${leaderLap}/${sd.total_laps}</span>`
    } else if (sd.session_time_left != null) {
      const mins = Math.floor(sd.session_time_left / 60)
      const secs = sd.session_time_left % 60
      leftInfo = `<span class="text-xs font-mono text-white">${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}</span>`
    }

    let scBadge = ""
    if (sd.safety_car_status === 1) {
      scBadge = `<span class="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold bg-yellow-500/20 text-yellow-400">SC</span>`
    } else if (sd.safety_car_status === 2) {
      scBadge = `<span class="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold bg-yellow-500/20 text-yellow-400">VSC</span>`
    } else if (sd.safety_car_status === 3) {
      scBadge = `<span class="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold bg-red-500/20 text-red-400">RED</span>`
    }

    // Right side: tracked car stats
    const trackedIdx = this.trackedCarIndexValue
    const tracked = cars.find((c) => c.car_index === trackedIdx)
    let rightInfo = ""
    if (tracked) {
      const pos = `P${tracked.position}`
      const last =
        tracked.last_lap_time_ms > 0
          ? this.formatLapTime(tracked.last_lap_time_ms)
          : "-"
      const best = this.personalBestLaps[trackedIdx]
        ? this.formatLapTime(this.personalBestLaps[trackedIdx])
        : "-"
      const classBest =
        this.overallBestLap < Infinity
          ? this.formatLapTime(this.overallBestLap)
          : "-"
      rightInfo = `<span class="text-xs font-mono text-white/60">${pos} | Last: <span class="text-white">${last}</span> | Best: <span class="text-green-400">${best}</span> | Top: <span class="text-fuchsia-500">${classBest}</span></span>`
    }

    if (!leftInfo && !scBadge && !rightInfo) return ""

    return `<div class="flex items-center justify-between px-2 py-1.5 border-b border-white/10 bg-white/[0.02]">
      <div class="flex items-center gap-2">${leftInfo}${scBadge}</div>
      <div>${rightInfo}</div>
    </div>`
  }

  renderRow(car) {
    const idx = car.car_index
    const isTracked = idx === this.trackedCarIndexValue
    const teamColor =
      F1_TEAM_COLORS[car.team_id] ||
      getComputedStyle(document.documentElement)
        .getPropertyValue("--app-text-muted")
        .trim()

    const gap =
      car.position === 1 ? "" : `+${this.formatTime(car.gap_to_leader_ms)}`
    const interval =
      car.position === 1 ? "" : `+${this.formatTime(car.gap_to_front_ms)}`

    const bestLapMs = this.personalBestLaps[idx]
    const bestLap = bestLapMs ? this.formatLapTime(bestLapMs) : "-"
    const bestLapBg =
      bestLapMs && bestLapMs <= this.overallBestLap ? "bg-fuchsia-500/10" : ""

    const lastLap =
      car.last_lap_time_ms > 0 ? this.formatLapTime(car.last_lap_time_ms) : "-"
    const lastLapClass = this.lapColor(car.last_lap_time_ms, idx)

    const s1 = car.sector1_ms ? this.formatSector(car.sector1_ms) : "-"
    const s2 = car.sector2_ms ? this.formatSector(car.sector2_ms) : "-"
    const s3 = car.sector3_ms ? this.formatSector(car.sector3_ms) : "-"
    const s1Class = this.sectorColor(car.sector1_ms, idx, "s1")
    const s2Class = this.sectorColor(car.sector2_ms, idx, "s2")
    const s3Class = this.sectorColor(car.sector3_ms, idx, "s3")

    // Pit info
    let pitInfo = ""
    if (car.pit_status > 0) {
      pitInfo = `<span class="inline-flex items-center rounded px-1 py-0.5 text-[10px] font-medium bg-yellow-500/20 text-yellow-400">PIT</span>`
    } else if (this.lastPitLap[idx]) {
      pitInfo = `<span class="text-[10px] text-white/40">L${this.lastPitLap[idx]}</span>`
    }

    const rowBg = isTracked ? "bg-white/[0.08]" : ""

    return `<tr class="border-b border-white/5 hover:bg-white/5 transition-colors ${rowBg}">
      <td class="py-1.5 px-2 text-center text-xs font-bold text-white" style="border-left: 3px solid ${teamColor}">${car.position}</td>
      <td class="py-1.5 px-2 text-xs text-white truncate overflow-hidden">${car.driver_name || `Car ${idx}`}</td>
      <td class="py-1.5 px-2 text-center text-xs">${pitInfo}</td>
      <td class="py-1.5 px-2 text-center text-xs font-mono text-white/60">L${car.current_lap}</td>
      <td class="py-1.5 px-2 text-right text-xs font-mono text-white/60">${gap}</td>
      <td class="py-1.5 px-2 text-right text-xs font-mono text-white/60">${interval}</td>
      <td class="py-1.5 px-2 text-right text-xs font-mono text-green-400 ${bestLapBg}">${bestLap}</td>
      <td class="py-1.5 px-2 text-right text-xs font-mono ${lastLapClass}">${lastLap}</td>
      <td class="py-1.5 px-2 text-right text-xs font-mono ${s1Class}">${s1}</td>
      <td class="py-1.5 px-2 text-right text-xs font-mono ${s2Class}">${s2}</td>
      <td class="py-1.5 px-2 text-right text-xs font-mono ${s3Class}">${s3}</td>
    </tr>`
  }

  formatLapTime(ms) {
    if (!ms || ms === 0) return "-"
    const minutes = Math.floor(ms / 60000)
    const seconds = Math.floor((ms % 60000) / 1000)
    const millis = ms % 1000
    if (minutes > 0) {
      return `${minutes}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(3, "0")}`
    }
    return `${seconds}.${String(millis).padStart(3, "0")}`
  }

  formatSector(ms) {
    if (!ms || ms === 0) return "-"
    const seconds = Math.floor(ms / 1000)
    const millis = ms % 1000
    return `${seconds}.${String(millis).padStart(3, "0")}`
  }

  formatTime(ms) {
    if (!ms || ms === 0) return "0.000"
    const seconds = Math.floor(ms / 1000)
    const millis = ms % 1000
    if (seconds >= 60) {
      const min = Math.floor(seconds / 60)
      const sec = seconds % 60
      return `${min}:${String(sec).padStart(2, "0")}.${String(millis).padStart(3, "0")}`
    }
    return `${seconds}.${String(millis).padStart(3, "0")}`
  }
}
